import { AnchorButton, Tree, TreeNodeInfo } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import cloneDeep from 'lodash.clonedeep';
import * as React from 'react';
import { MosaicKey } from 'react-mosaic-component/lib/types';
import { useHistory } from 'react-router-dom';
import { Tooltip } from 'vet-bones/components';
import { noop, stringToRegExp } from 'vet-bones/utils';

import { ItemQueryList } from 'src/components/select/ItemQueryList';
import { PROJECT_MAPPING_EXPAND_MAPPINGS_PARAM } from 'src/constants/project';
import { TEST_IDS } from 'src/constants/testIds';
import { useNewAddResourceMapping } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useAddResourceMapping';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { isMapping } from 'src/containers/ProjectDiagram/utils/data';
import { getMappingTargetIri } from 'src/containers/ProjectDiagram/utils/mapping/getMappingTargetIri';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import {
  ProjectMappingTreeNodeData,
  projectMappingResourcesTreeSelector,
} from 'src/selectors/projectMappingResourcesTree';
import { projectResourceSelectItems } from 'src/selectors/projectResource';
import * as copy from 'src/templates/copy';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { getProjectMappingRoute } from 'src/utils/routes/getProjectMappingRoute';
import {
  TreeNodeId,
  filterEmptyNodes,
  findMapNode,
  forEachNode,
} from 'src/utils/tree';

type ProjectMappingSidebarProps = { id: MosaicKey };

const SidebarQueryList =
  ItemQueryList.ofType<TreeNodeInfo<ProjectMappingTreeNodeData>>();

export const ProjectMappingSidebar: React.VFC<
  ProjectMappingSidebarProps
> = () => {
  const { projectId, mappingId } = useRouterParams<'PROJECT_MAPPING'>();
  const diagram = useDiagram();
  const history = useHistory();

  const defaultPortalConnection = useDefaultPortalConnection();
  const isAutoMappingMultipleTablesSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isAutoMappingMultipleTablesSupported',
      defaultPortalConnection
    )
  );

  const [expandedNodeIds, setExpandedNodeIds] = React.useState<
    Record<TreeNodeId, boolean>
  >(() => {
    if (!diagram) {
      return {};
    }
    const { model } = diagram;
    const connectedMappings = model.linkDataArray.filter(
      (linkData): linkData is Mapping & { to: string } =>
        isMapping(linkData) && Boolean(linkData.to)
    );
    return connectedMappings.reduce<Record<string, boolean>>((acc, mapping) => {
      const resourceValue = getResourceValue(mapping.from);
      const modelConceptValue = getMappingTargetIri(
        mapping.to,
        model.nodeDataArray
      );

      if (!mappingId || mappingId === mapping.value) {
        acc[resourceValue] = true;
        if (modelConceptValue) {
          acc[`${resourceValue}-${modelConceptValue}`] = true;
        }
      }

      return acc;
    }, {});
  });
  const [filterClearPaths, setFilterClearPaths] =
    React.useState<Record<string, boolean>>();

  const resourceItems = useReduxSelector(projectResourceSelectItems(projectId));
  const resourcesTree = useReduxSelector(
    projectMappingResourcesTreeSelector(projectId, expandedNodeIds, mappingId)
  );

  const expandSelectedMappingId = React.useCallback(() => {
    if (!mappingId) {
      return;
    }

    const info = findMapNode(resourcesTree, (node, parentNodes) =>
      mappingId === node.nodeData?.mappingId ? { node, parentNodes } : undefined
    );
    if (info) {
      setExpandedNodeIds((prevIds) => ({
        ...prevIds,
        ...Object.fromEntries(info.parentNodes.map(({ id }) => [id, true])),
      }));
    }
  }, [mappingId, resourcesTree]);

  // Handle mappingId updates
  const didMappingIdChange = useDidValueChange(mappingId);
  React.useEffect(() => {
    if (didMappingIdChange) {
      expandSelectedMappingId();
    }
  }, [didMappingIdChange, expandSelectedMappingId]);

  const allNodesExpanded = React.useMemo(() => {
    const paths: Record<string, boolean> = {};
    forEachNode(resourcesTree, (node) => {
      paths[node.id] = true;
    });
    return paths;
  }, [resourcesTree]);

  React.useEffect(() => {
    const { search, pathname } = history.location;
    const params = new URLSearchParams(search);

    if (params.has(PROJECT_MAPPING_EXPAND_MAPPINGS_PARAM)) {
      setExpandedNodeIds(allNodesExpanded);
      history.replace(pathname);
    }
  }, [history, allNodesExpanded]);

  const handleAddMapping = useNewAddResourceMapping();

  return (
    <SidebarQueryList
      collapsibleInput
      customRenderer={({ itemList }, queryInputElement, isInputCollapsed) => (
        <div
          className="sd-project-mapping-sidebar"
          data-testid={TEST_IDS.projectMapping.sidebar.container}
        >
          <div className="sd-project-mapping-sidebar__header">
            <Tooltip content={copy.components.projectMapping.sidebar.expandAll}>
              <AnchorButton
                data-testid={TEST_IDS.projectMapping.sidebar.expandAll}
                icon={IconNames.EXPAND_ALL}
                minimal
                onClick={() => setExpandedNodeIds(allNodesExpanded)}
              />
            </Tooltip>
            <Tooltip
              content={copy.components.projectMapping.sidebar.collapseAll}
            >
              <AnchorButton
                data-testid={TEST_IDS.projectMapping.sidebar.collapseAll}
                icon={IconNames.COLLAPSE_ALL}
                minimal
                onClick={() => setExpandedNodeIds({})}
              />
            </Tooltip>
            <div
              className="sd-project-mapping-sidebar__header--filter"
              data-testid={TEST_IDS.projectMapping.sidebar.filter}
            >
              {queryInputElement}
            </div>
            {isInputCollapsed && resourceItems.length > 0 ? (
              <Tooltip
                content={
                  copy.components.projectMapping.sidebar
                    .resourceSuggestionsDisabled
                }
                disabled={isAutoMappingMultipleTablesSupported}
              >
                <AnchorButton
                  className="sd-project-mapping-sidebar__header--button"
                  data-testid={TEST_IDS.projectMapping.sidebar.newMapping}
                  disabled={!isAutoMappingMultipleTablesSupported}
                  minimal
                  onClick={() => {
                    handleAddMapping();
                  }}
                  outlined
                  text={copy.components.projectMapping.sidebar.newMapping}
                />
              </Tooltip>
            ) : null}
          </div>
          {itemList}
        </div>
      )}
      debounceOnInputChange={300}
      itemListPredicate={(query, items) => {
        const isMatch = (text: string) =>
          Boolean(text.match(stringToRegExp(query, 'i')));

        if (query === '') {
          return items;
        }

        // Remove nodes with no match on any level
        return cloneDeep(items)
          .map((resource) => {
            const hasResourceMatch = isMatch(resource.label as string);
            return {
              ...resource,
              childNodes: resource.childNodes
                ?.map((modelConcept) => {
                  const hasModelConceptMatch = isMatch(
                    modelConcept.label as string
                  );
                  return {
                    ...modelConcept,
                    childNodes: modelConcept.childNodes?.filter(
                      (node) =>
                        hasResourceMatch ||
                        hasModelConceptMatch ||
                        isMatch(node.label as string)
                    ),
                  };
                })
                .filter(filterEmptyNodes),
            };
          })
          .filter(filterEmptyNodes);
      }}
      itemListRenderer={({ filteredItems }) => (
        <Tree<ProjectMappingTreeNodeData>
          contents={filteredItems}
          onNodeClick={(node) => {
            const { mappingId } = node.nodeData ?? {};
            if (mappingId) {
              history.push(getProjectMappingRoute(projectId, mappingId));
              return;
            }
            setExpandedNodeIds((prevPaths) => ({
              ...prevPaths,
              [node.id]: !node.isExpanded,
            }));
          }}
          onNodeCollapse={(node) => {
            setExpandedNodeIds((prevPaths) => ({
              ...prevPaths,
              [node.id]: false,
            }));
          }}
          onNodeExpand={(node) => {
            setExpandedNodeIds((prevPaths) => ({
              ...prevPaths,
              [node.id]: true,
            }));
          }}
        />
      )}
      items={resourcesTree}
      onQueryChange={(newQuery) => {
        if (newQuery === '') {
          if (filterClearPaths) {
            // Reset expanded paths from before filtering
            setExpandedNodeIds(filterClearPaths);
            expandSelectedMappingId();
          }
          setFilterClearPaths(undefined);
        } else {
          if (!filterClearPaths) {
            // Save current expanded state for when query is cleared
            setFilterClearPaths(expandedNodeIds);
          }
          setExpandedNodeIds(allNodesExpanded);
        }
      }}
      onSelect={noop}
    />
  );
};
